import React from "react";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import EmailIcon from "@mui/icons-material/Email";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkIcon from "@mui/icons-material/Link";

// Standardized action-icon styling. Reference: Record View Edit/Delete
// buttons + the PDF Viewer Approve/Reject/Link toolbar (28x28, rounded,
// tinted background + colored border, 18px icon, centered).
// bg/border switch between enabled/disabled; icon color is always the
// action's own color — disabled state never grays out the icon itself.
const VARIANT_STYLES = {
  edit: { bg: "bg-blue-100", border: "border-blue-200", icon: "text-blue-400" },
  delete: { bg: "bg-red-100", border: "border-red-200", icon: "text-red-400" },
  view: { bg: "bg-yellow-100", border: "border-yellow-300", icon: "text-yellow-600" },
  copy: { bg: "bg-purple-100", border: "border-purple-200", icon: "text-purple-500" },
  download: { bg: "bg-emerald-100", border: "border-emerald-200", icon: "text-emerald-500" },
  upload: { bg: "bg-emerald-100", border: "border-emerald-200", icon: "text-emerald-500" },
  email: { bg: "bg-blue-100", border: "border-blue-200", icon: "text-blue-400" },
  restore: { bg: "bg-blue-100", border: "border-blue-200", icon: "text-blue-400" },
  approve: { bg: "bg-emerald-100", border: "border-emerald-200", icon: "text-emerald-500" },
  reject: { bg: "bg-red-100", border: "border-red-200", icon: "text-red-400" },
  link: { bg: "bg-cyan-100", border: "border-cyan-200", icon: "text-cyan-500" },
  relink: { bg: "bg-cyan-100", border: "border-cyan-200", icon: "text-cyan-500" },
};

const ICONS = {
  edit: EditIcon,
  delete: DeleteIcon,
  view: VisibilityIcon,
  copy: ContentCopyIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  email: EmailIcon,
  restore: RestoreIcon,
  approve: CheckCircleIcon,
  reject: CancelIcon,
  link: LinkIcon,
  relink: LinkIcon,
};

// Disabled state: very light/subtle — pale background, pale border, pale
// icon color, no hover, "not-allowed" cursor. Distinct from every action's
// own tinted color so disabled reads as clearly inert at a glance.
const DISABLED_STYLE = {
  background: "#f7f8fa",
  color: "#c1c6ce",
  border: "1px solid #edf0f2",
  opacity: 1,
};

const ActionIconButton = React.forwardRef(function ActionIconButton(
  {
    action,
    icon,
    onClick,
    title,
    disabledReason,
    disabled = false,
    className = "",
    iconSx,
    style,
    ...rest
  },
  ref
) {
  const Icon = icon || ICONS[action];
  const variant = VARIANT_STYLES[action] || VARIANT_STYLES.view;
  const colorClasses = disabled ? "" : `${variant.bg} ${variant.border} ${variant.icon}`;
  const tooltipTitle = disabled ? disabledReason || title : title;

  if (!Icon) return null;

  return (
    <div
      ref={ref}
      role="button"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      aria-label={tooltipTitle}
      title={tooltipTitle}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if ((e.key === "Enter" || e.key === " ") && onClick) {
                e.preventDefault();
                onClick(e);
              }
            }
      }
      style={disabled ? { ...DISABLED_STYLE, ...style } : style}
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${colorClasses} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      } ${className}`}
      {...rest}
    >
      <Icon
        className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
        sx={{ fontSize: 18, color: disabled ? "inherit" : undefined, ...iconSx }}
      />
    </div>
  );
});

export const EditActionButton = (props) => (
  <ActionIconButton action="edit" title="Edit" {...props} />
);
export const DeleteActionButton = (props) => (
  <ActionIconButton action="delete" title="Delete" {...props} />
);
export const ViewActionButton = (props) => (
  <ActionIconButton action="view" title="View" {...props} />
);
export const CopyActionButton = (props) => (
  <ActionIconButton action="copy" title="Copy" {...props} />
);
export const DownloadActionButton = (props) => (
  <ActionIconButton action="download" title="Download" {...props} />
);
export const UploadActionButton = (props) => (
  <ActionIconButton action="upload" title="Upload" {...props} />
);
export const EmailActionButton = (props) => (
  <ActionIconButton action="email" title="Email" {...props} />
);
export const RestoreActionButton = (props) => (
  <ActionIconButton action="restore" title="Restore" {...props} />
);
export const ApproveActionButton = (props) => (
  <ActionIconButton action="approve" title="Approve" {...props} />
);
export const RejectActionButton = (props) => (
  <ActionIconButton action="reject" title="Reject" {...props} />
);
export const LinkActionButton = (props) => (
  <ActionIconButton action="link" title="Link Request" {...props} />
);
export const RelinkActionButton = (props) => (
  <ActionIconButton action="relink" title="Relink Request" {...props} />
);

export default ActionIconButton;
